'use client';

import React, { useEffect } from 'react';
import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import { useSelector } from 'react-redux';
import { useDrawer } from 'src/hooks/drawers/useDrawer';
import useFilterSets from 'src/hooks/filters/useFilterSets';
import { LOCATION } from 'src/hooks/filters/constants';
import { PAYER_NAME_FILTER_LOCATIONS } from '../../constants';
import { useAppDispatch, activateLocation } from 'src/redux';
import { selectActiveLocation, selectModeType } from 'src/redux/slices/analyticsSlice';
import { Drawer } from 'src/components';
import { IconButton } from 'src/components/PulseUI/IconButton';
import {
  Specialties,
  SiteSpecialties,
  PayerType,
  PayerName,
  HospitalSystems,
  GPOAffiliations,
  MedicalSchools,
  Residency,
  Fellowship,
  SiteOfCare,
  Inpatient,
} from 'src/components/FilterSets';
import { PresetsDropdown } from 'src/components/Discovery/components/PresetsDropdown';

const ANALYTICS_LOCATION = LOCATION.TERRITORY_ANALYTICS;

const AnalyticsFilterDrawer = () => {
  const dispatch = useAppDispatch();
  const { isDrawerOpen, closeDrawer } = useDrawer();
  const { applyFilters, resetToApplied } = useFilterSets(ANALYTICS_LOCATION);

  // Read the in-view table's mode (registered by each table's header) so
  // INPATIENT can be gated to treating mode.
  const activeLocation = useSelector(selectActiveLocation);
  const modeType = useSelector(selectModeType(activeLocation));
  const isTreating = modeType !== 'sending';

  const showPayerName = PAYER_NAME_FILTER_LOCATIONS.includes(activeLocation);

  const drawerOpen = isDrawerOpen('analytics-filter-drawer');

  useEffect(() => {
    if (drawerOpen) {
      dispatch(activateLocation(ANALYTICS_LOCATION));
    }
  }, [dispatch, drawerOpen]);

  const handleClose = () => {
    closeDrawer('analytics-filter-drawer');
  };

  const handleCancel = () => {
    resetToApplied();
    handleClose();
  };

  const handleApply = () => {
    applyFilters();
    handleClose();
  };

  if (!drawerOpen) return null;

  return (
    <Drawer
      isOpen={drawerOpen}
      onClose={handleClose}
      anchor="right"
      size="base2"
      variant="persistent"
      className="z-50 absolute right-0 top-0 bottom-0"
    >
      {/* Drawer Header */}
      <Drawer.Header
        className="bg-sky-100 text-primary border-b-0"
        data-testid="analytics-filter-drawer-header"
      >
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-2">
            <p
              className="font-bold text-lg"
              data-testid="analytics-filter-drawer-title"
            >
              Filters
            </p>
          </div>
          <div className="flex items-center gap-2">
            <IconButton
              variant="plain"
              color="neutral"
              size="sm"
              onClick={handleClose}
              icon={faXmark}
              className="text-primary hover:bg-sky-200"
              data-testid="analytics-filter-drawer-close-button"
            />
          </div>
        </div>
      </Drawer.Header>

      {/* Drawer Content */}
      <Drawer.Content className="flex flex-col overflow-hidden">
        {/* Presets section - fixed */}
        <div
          className="self-stretch p-6 bg-white inline-flex flex-col justify-start items-start gap-2"
          data-testid="analytics-presets-section"
        >
          <div className="self-stretch inline-flex justify-center items-center gap-2">
            <div className="flex-1 justify-start text-primary text-base font-bold font-['Inter'] leading-normal">
              Presets
            </div>
          </div>
          <PresetsDropdown
            type="FILTER"
            location={ANALYTICS_LOCATION}
            readOnly
          />
        </div>

        {/* Filters section - scrollable */}
        <div className="flex-1 overflow-y-auto">
          <div className="w-full px-6 py-4 inline-flex flex-col gap-4">
            <SiteOfCare location={ANALYTICS_LOCATION} />
            {isTreating && <Inpatient location={ANALYTICS_LOCATION} />}
            <PayerType location={ANALYTICS_LOCATION} />
            {showPayerName && <PayerName location={ANALYTICS_LOCATION} />}
            <Specialties location={ANALYTICS_LOCATION} />
            <SiteSpecialties location={ANALYTICS_LOCATION} />
            <HospitalSystems location={ANALYTICS_LOCATION} />
            <GPOAffiliations location={ANALYTICS_LOCATION} />
            <MedicalSchools location={ANALYTICS_LOCATION} />
            <Residency location={ANALYTICS_LOCATION} />
            <Fellowship location={ANALYTICS_LOCATION} />
          </div>
        </div>
      </Drawer.Content>
      <Drawer.Footer className="px-6 py-4 bg-white border-t border-neutral-200">
        <div className="w-full flex justify-between items-center gap-2">
          <button
            className="w-full rounded-full px-4 py-2 bg-white text-sm font-medium text-primary border border-neutral-200 hover:bg-neutral-100"
            onClick={handleCancel}
            data-testid="analytics-filter-drawer-cancel-button"
          >
            Cancel
          </button>
          <button
            className="w-full rounded-full px-4 py-2 bg-sky-600 text-sm font-medium text-white hover:bg-sky-700"
            onClick={handleApply}
            data-testid="analytics-filter-drawer-apply-button"
          >
            Apply
          </button>
        </div>
      </Drawer.Footer>
    </Drawer>
  );
};

export default AnalyticsFilterDrawer;
